import useAudioData from './useAudioData';
import styles from './Audio.module.css';
import { MediaBadge } from '@round/whitelabel-ui';
import { AudioPlayerProvider } from 'Modules/AudioPlayer/AudioPlayerContext';
import AudioPlaybackThumbnail from 'Modules/AudioPlayer/AudioPlaybackThumbnail/AudioPlaybackThumbnail';
import { SkeletonizedValue } from '@round/ui-kit';
import { buildTiktokMusicUrl } from 'helpers';

type Props = {
    audioId: number | null | undefined;
};

const Audio = ({ audioId }: Props) => {
    const {
        results: [audioQuery, assetQuery],
        helpers,
    } = useAudioData(audioId);

    const audioPlatformUrl = audioQuery.isSuccess
        ? buildTiktokMusicUrl(audioQuery.data.title ?? '', audioQuery.data.tiktok_id)
        : '';

    const imageUrl = assetQuery.isSuccess
        ? assetQuery.data.image_thumb.cached_url || assetQuery.data.image_thumb.original_url || ''
        : '';
    const audioUrl = assetQuery.isSuccess
        ? assetQuery.data.audio_file.cached_url || assetQuery.data.audio_file.original_url || ''
        : '';

    const errorTitle = !helpers.audio.isExpectedErrorResponse(audioQuery.error)
        ? 'Something went wrong'
        : audioQuery.error.response.status === 404
          ? 'Audio not found'
          : 'Access denied';

    const errorMessage = !helpers.audio.isExpectedErrorResponse(audioQuery.error)
        ? 'An unexpected error occurred. Please try again later.'
        : audioQuery.error.response.status === 404
          ? 'Sorry, we couldn’t find any data for this audio'
          : 'Sorry, you don’t have permission to view this audio';

    return (
        <AudioPlayerProvider>
            <div className={styles.container}>
                <AudioPlaybackThumbnail
                    skeletonCircle={false}
                    loading={assetQuery.isPending}
                    className={styles.image}
                    imageUrl={imageUrl}
                    alt={audioQuery.data?.title ?? ''}
                    audioUrl={audioUrl}
                />

                <div className={styles.info}>
                    {!audioQuery.isError && (
                        <>
                            <a
                                className={styles.title}
                                href={audioPlatformUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                                onClick={(e) => {
                                    if (!audioQuery.isSuccess || !audioPlatformUrl.length) {
                                        e.preventDefault();
                                    }
                                }}
                            >
                                <SkeletonizedValue isInitialized={!audioQuery.isPending}>
                                    {audioQuery.data?.title ?? audioQuery.data?.tiktok_id ?? '-'}
                                </SkeletonizedValue>
                            </a>
                            <p className={styles.subTitle}>
                                <SkeletonizedValue isInitialized={!audioQuery.isPending}>
                                    {audioQuery.data?.author_name ?? '-'}
                                </SkeletonizedValue>
                            </p>
                            <div className={styles.badges}>
                                <MediaBadge type="audio" />
                                <MediaBadge type="tiktok" />
                                {typeof audioQuery.data?.is_original === 'boolean' ? (
                                    <MediaBadge type={audioQuery.data.is_original ? 'original' : 'official'} />
                                ) : null}
                            </div>
                        </>
                    )}

                    {audioQuery.isError && (
                        <>
                            <p className={styles.title}>{errorTitle}</p>
                            <p className={styles.subTitle}>{errorMessage}</p>
                        </>
                    )}
                </div>
            </div>
        </AudioPlayerProvider>
    );
};

export default Audio;
